import { RpcError, RpcMetadata, ServerStreamingCall } from '@protobuf-ts/runtime-rpc'
import { List, Map as ImmutableMap } from 'immutable'
import { NotifyRequest, NotifyResponse } from 'pbts/c1/api/notify/v1/notify'
import { NotifyClient as NotifyServiceClientPb } from 'pbts/c1/api/notify/v1/notify.client'
import { useEffect, useRef } from 'react'
import { useClientRPC } from './rpcts'

export type EventHandler = () => void

export interface NotifyClient {
    subscribe(token: string[], handler: EventHandler): () => void

    runHandler(token: string): void

    runHandlers(): void
}

type subscriptions = ImmutableMap<string, List<EventHandler>>

enum connectionStatus {
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    DISCONNECTED = 'disconnected',
    CLOSED = 'closed',
}

enum RPCErrorCode {
    CANCELLED = 'CANCELLED',
}

// Connection wraps the notification service client, abstracting most
// of the complexity away from the caller and handling all internal
// state management/subscriptions/reconnections/etc.
class NotifyConn {
    client: NotifyServiceClientPb | null

    call: ServerStreamingCall<NotifyRequest, NotifyResponse> | null

    callAbort: AbortController | null

    status: connectionStatus

    offset: string | null

    reconnectCount: number

    reconnectTimer: ReturnType<typeof setTimeout> | null

    keepAliveTimer: ReturnType<typeof setTimeout> | null

    subscriptions: subscriptions

    constructor() {
        this.client = null
        this.call = null
        this.callAbort = null

        this.status = connectionStatus.DISCONNECTED
        this.offset = null

        this.reconnectCount = 0
        this.reconnectTimer = null
        this.keepAliveTimer = null

        this.subscriptions = ImmutableMap<string, List<EventHandler>>()
    }

    setClient(c: NotifyServiceClientPb | undefined): void {
        this.disconnect()

        if (c) {
            this.client = c
            this.reconnect()
        }
    }

    // given our current reconnect count, calculate a backoff wait time (in seconds)
    reconnectWait(): number {
        const wait = this.reconnectWaitNoIncrement()
        this.reconnectCount++
        return wait
    }

    // given our current reconnect count, calculate a backoff wait time (in seconds)
    reconnectWaitNoIncrement(): number {
        const wait = Math.min(this.reconnectCount ** 2, 60)
        // We add 1 here so that in the 0 case we make sure the `setInterval` still delays at least one millisecond
        return wait * 1000 + 1
    }

    cancelTimers(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        if (this.keepAliveTimer) {
            clearTimeout(this.keepAliveTimer)
            this.keepAliveTimer = null
        }
    }

    close(): void {
        this.disconnect()
        this.subscriptions = this.subscriptions.clear()
    }

    disconnect(): void {
        if (this.callAbort) {
            this.callAbort.abort()
            this.call = null
            this.callAbort = null
        }
        this.cancelTimers()
        this.status = connectionStatus.DISCONNECTED
    }

    instantReconnect(): void {
        this._reconnectInternal(true)
    }

    reconnect(): void {
        this._reconnectInternal(false)
    }

    _reconnectInternal(instant: boolean): void {
        this.disconnect()

        // No client, nothing to do here.
        if (!this.client) {
            return
        }

        // no subs, nothing to do here.
        if (this.subscriptions.size === 0) {
            return
        }

        // always backoff slightly
        this.cancelTimers()
        let waitDuration
        if (instant) {
            waitDuration = this.reconnectWaitNoIncrement()
        } else {
            waitDuration = this.reconnectWait()
        }
        this.reconnectTimer = setTimeout(() => {
            this.dial()
        }, waitDuration)

        this.status = connectionStatus.CONNECTING
    }

    makeRequest(): NotifyRequest {
        const req = NotifyRequest.create({
            tokens: Array.from(this.subscriptions.keys()),
        })

        if (this.offset) {
            req.offset = this.offset
        }

        return req
    }

    keepAlive(): void {
        const duration = 30 * 1000

        if (this.keepAliveTimer) {
            clearTimeout(this.keepAliveTimer)
            this.keepAliveTimer = null
        }

        this.keepAliveTimer = setTimeout(() => {
            this.reconnect()
        }, duration)
    }

    runHandler(token: string): void {
        const handlers = this.subscriptions.get(token)
        if (!handlers) {
            return
        }
        handlers.forEach((handler) => handler())
    }

    runHandlers(): void {
        this.subscriptions.forEach((handlers: List<EventHandler>) => {
            handlers.forEach((handler) => handler())
        })
    }

    async dial(): Promise<void> {
        // No client, nothing to do here.
        if (!this.client) {
            this.disconnect()
            return
        }

        if (this.callAbort) {
            this.callAbort.abort()
            this.callAbort = null
            this.call = null
        }

        this.callAbort = new AbortController()
        const req = this.makeRequest()

        this.call = this.client.notify(req, { abort: this.callAbort.signal })
        // grpc-web has an unfortunate bug where when the proxy returns a response with an empty body (e.g. a 503
        // during a service restart), it doesn't close the connection. grpc-web reads this response just fine, but
        // doesn't close it's side of the connection either, so we end up waiting for the envoy idleTimeout. We have
        // to manage a keepAlive timer as a result, that we constantly advance as we get data from the stream.
        this.cancelTimers()
        this.keepAlive()

        this.call.responses.onMessage((response: NotifyResponse) => {
            this.keepAlive()
            this.reconnectCount = 0
            this.status = connectionStatus.CONNECTED
            this.offset = response.offset

            switch (response.msg.oneofKind) {
                case 'changed': {
                    const changed = response.msg.changed
                    const handlers = this.subscriptions.get(changed)
                    if (!handlers) {
                        return
                    }
                    handlers.forEach((handler) => handler())
                    break
                }
                // no default
            }
        })

        this.call.responses.onError((_error: Error): void => {
            if (_error instanceof RpcError && _error.code === RPCErrorCode.CANCELLED) {
                this.instantReconnect()
                return
            }
            this.reconnect()
        })

        this.call.responses.onComplete(() => {
            this.reconnect()
        })

        const doCall = async (): Promise<void> => {
            try {
                await this.call
            } catch (_err) {
                if (_err instanceof RpcError && _err.code === RPCErrorCode.CANCELLED) {
                    this.instantReconnect()
                } else {
                    this.reconnect()
                }
            }
        }
        doCall()
    }

    subscribe(tokens: string[], handler: EventHandler): () => void {
        if (!tokens.length) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            return (): void => {}
        }

        for (const token of tokens) {
            let handlers = this.subscriptions.get(token)
            if (!handlers) {
                handlers = List<EventHandler>()
            }
            handlers = handlers.push(handler)
            this.subscriptions = this.subscriptions.set(token, handlers)
        }

        this.instantReconnect()

        return (): void => {
            for (const token of tokens) {
                // remove the subscription
                let handlers = this.subscriptions.get(token)
                if (!handlers) {
                    return
                }
                handlers = handlers.filterNot((subscribed) => {
                    return subscribed === handler
                })

                if (handlers.size > 0) {
                    this.subscriptions = this.subscriptions.set(token, handlers)
                }

                this.subscriptions = this.subscriptions.delete(token)
            }

            // don't bother with reconnect() on unsubscribe: we'll likely be immediately
            // re-subscribing to a different token, or we're getting unmounted entirely
            if (this.subscriptions.size === 0) {
                this.close()
            }
        }
    }
}

export function useNotify(): NotifyClient {
    const conn = useRef<NotifyConn>(new NotifyConn())
    const client = useClientRPC(NotifyServiceClientPb)

    const setupConn = (): (() => void) | undefined => {
        if (!client) {
            return
        }
        conn.current.setClient(client)
        return // eslint-disable-line no-useless-return
    }

    useEffect(setupConn, [client])
    return conn.current
}

const getKeys = (md: RpcMetadata, key: string): string[] => {
    const v = md[key]
    if (!v) {
        return []
    }
    if (Array.isArray(v)) {
        return v
    }
    return v.split(',').map((s: string) => s.trim())
}

const c1NotifyHeader = 'c1-notify-token'

export const getNotifyTokens = (md: RpcMetadata): string[] => {
    return getKeys(md, c1NotifyHeader)
}
